"use client";

import type { useChat } from "@ai-sdk/react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { OrderDirectionLabels } from "@/app/[lang]/[workspaceId]/sa/constants";
import {
  BrowserCategoryLabels,
  DefaultChannelGroupLabels,
  DeviceCategoryLabels,
  OSCategoryLabels,
  SessionPerformanceDetailGroupByLabels,
  SessionPerformanceDetailOrderByFieldLabels,
  UserAnalyticsDataDateTypeLabels,
  UserAnalyticsFilterLabels,
} from "@/app/[lang]/[workspaceId]/ua/constants";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { toProjectIdFromUaViewId } from "../shared/ua-view-id";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { UpdateUserAnalyticsViewInput } from "./schema";

const ProjectDataQuery = graphql(`
  query UaViewUpdateConfirmProjectData($id: ID!) {
    node(id: $id) {
      __typename
      ... on UserAnalyticsProject {
        name
        userAnalytics {
          id
          __typename
          urlGroups { id __typename name }
        }
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function UpdateUserAnalyticsViewConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const isTarget =
    isToolUIPart(part) && getToolName(part) === "update_user_analytics_view";
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<UpdateUserAnalyticsViewInput>;

  const projectId = input.id ? toProjectIdFromUaViewId(input.id) : null;
  const [{ data, fetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectId ?? "" },
    pause: !(isTarget && isInputAvailable && projectId),
  });
  const project =
    data?.node?.__typename === "UserAnalyticsProject" ? data.node : null;
  const projectName = project?.name ?? "Unknown";
  const urlGroups = project?.userAnalytics?.urlGroups ?? [];
  const urlGroupNames = Object.fromEntries(
    urlGroups.map((group) => [group.id, group.name]),
  );

  if (!isTarget || !isInputAvailable) return null;

  const detailGroupLabels =
    input.detailGroupBy?.map((group) =>
      i18n._(SessionPerformanceDetailGroupByLabels[group]),
    ) ?? [];

  const detailOrderByLabel = input.detailOrderBy
    ? i18n._(
        SessionPerformanceDetailOrderByFieldLabels[input.detailOrderBy.field],
      )
    : null;

  const detailOrderDirectionLabel = input.detailOrderBy
    ? i18n._(OrderDirectionLabels[input.detailOrderBy.direction])
    : null;

  const campaignsSummary =
    input.campaigns?.map((campaign, index) => {
      const filter = input.campaignsFilterType?.[index];
      if (filter) {
        return `${campaign} (${i18n._(UserAnalyticsFilterLabels[filter])})`;
      }
      return campaign;
    }) ?? [];

  const eventParamsSummary =
    input.eventParams?.map((eventParam) => {
      const operator = i18n._(UserAnalyticsFilterLabels[eventParam.operator]);
      return `${eventParam.key} ${operator} ${eventParam.value}`;
    }) ?? [];

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to update this User Analytics view?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>View ID</Trans>: <span className="font-mono">{input.id}</span>
        </li>
        {projectId ? (
          <li>
            <Trans>Project</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-12" />
            ) : (
              <span className="font-medium">{projectName}</span>
            )}
          </li>
        ) : null}

        {typeof input.name === "string" ? (
          <li>
            <Trans>Name</Trans>:{" "}
            <span className="font-medium">{input.name}</span>
          </li>
        ) : null}
        {typeof input.description === "string" ? (
          <li>
            <Trans>Description</Trans>:{" "}
            <span className="font-normal">{input.description}</span>
          </li>
        ) : null}

        {input.defaultDateRange ? (
          <li>
            <Trans>Saved date</Trans>:{" "}
            <span className="font-medium">
              {i18n._(
                UserAnalyticsDataDateTypeLabels[input.defaultDateRange.type],
              )}
            </span>
            {input.defaultDateRange.type === "CUSTOM" &&
            input.defaultDateRange.dateRange ? (
              <span className="ml-2 text-muted-foreground">
                ({input.defaultDateRange.dateRange.start} -{" "}
                {input.defaultDateRange.dateRange.end})
              </span>
            ) : null}
          </li>
        ) : null}

        {"visitedPage" in input ? (
          <li>
            <Trans>Visited page</Trans>:{" "}
            <span className="font-medium break-all">
              {input.visitedPage ?? "(null)"}
            </span>
            {"visitedPageFilterType" in input && input.visitedPageFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(UserAnalyticsFilterLabels[input.visitedPageFilterType])}
              </span>
            ) : null}
          </li>
        ) : null}
        {"userPseudoId" in input ? (
          <li>
            <Trans>User ID</Trans>:{" "}
            <span className="font-medium break-all">
              {input.userPseudoId ?? "(null)"}
            </span>
            {"userPseudoIdFilterType" in input &&
            input.userPseudoIdFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[input.userPseudoIdFilterType],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {"sessionId" in input ? (
          <li>
            <Trans>Session ID</Trans>:{" "}
            <span className="font-medium break-all">
              {input.sessionId ?? "(null)"}
            </span>
            {"sessionIdFilterType" in input && input.sessionIdFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(UserAnalyticsFilterLabels[input.sessionIdFilterType])}
              </span>
            ) : null}
          </li>
        ) : null}

        {"firstPageLocationURL" in input ? (
          <li>
            <Trans>Landing page</Trans>:{" "}
            <span className="font-medium break-all">
              {input.firstPageLocationURL ?? "(null)"}
            </span>
            {"firstPageLocationFilterType" in input &&
            input.firstPageLocationFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[input.firstPageLocationFilterType],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {"firstPageLocationURLGroupIDs" in input ? (
          <li>
            <Trans>Landing page URL group</Trans>:{" "}
            <span className="font-medium">
              {(input.firstPageLocationURLGroupIDs ?? [])
                .map((id) => (id ? urlGroupNames[id] || id : ""))
                .filter(Boolean)
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"firstPageTitle" in input ? (
          <li>
            <Trans>Landing page title</Trans>:{" "}
            <span className="font-medium break-all">
              {input.firstPageTitle ?? "(null)"}
            </span>
            {"firstPageTitleFilterType" in input &&
            input.firstPageTitleFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[input.firstPageTitleFilterType],
                )}
              </span>
            ) : null}
          </li>
        ) : null}

        {"secondPageLocationURL" in input ? (
          <li>
            <Trans>Second page</Trans>:{" "}
            <span className="font-medium break-all">
              {input.secondPageLocationURL ?? "(null)"}
            </span>
            {"secondPageLocationFilterType" in input &&
            input.secondPageLocationFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[input.secondPageLocationFilterType],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {"secondPageLocationURLGroupIDs" in input ? (
          <li>
            <Trans>Second page URL group</Trans>:{" "}
            <span className="font-medium">
              {(input.secondPageLocationURLGroupIDs ?? [])
                .map((id) => (id ? urlGroupNames[id] || id : ""))
                .filter(Boolean)
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"secondPageTitle" in input ? (
          <li>
            <Trans>Second page title</Trans>:{" "}
            <span className="font-medium break-all">
              {input.secondPageTitle ?? "(null)"}
            </span>
            {"secondPageTitleFilterType" in input &&
            input.secondPageTitleFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[input.secondPageTitleFilterType],
                )}
              </span>
            ) : null}
          </li>
        ) : null}

        {"devices" in input ? (
          <li>
            <Trans>Devices</Trans>:{" "}
            <span className="font-medium">
              {(input.devices ?? [])
                .map((device) => i18n._(DeviceCategoryLabels[device]))
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"os" in input ? (
          <li>
            <Trans>OS</Trans>:{" "}
            <span className="font-medium">
              {(input.os ?? []).map((os) => i18n._(OSCategoryLabels[os]))}
            </span>
          </li>
        ) : null}
        {"browsers" in input ? (
          <li>
            <Trans>Browsers</Trans>:{" "}
            <span className="font-medium">
              {(input.browsers ?? [])
                .map((browser) => i18n._(BrowserCategoryLabels[browser]))
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}

        {"sources" in input ? (
          <li>
            <Trans>Sources</Trans>:{" "}
            <span className="font-medium">
              {(input.sources ?? []).join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"mediums" in input ? (
          <li>
            <Trans>Mediums</Trans>:{" "}
            <span className="font-medium">
              {(input.mediums ?? []).join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"terms" in input ? (
          <li>
            <Trans>Term</Trans>:{" "}
            <span className="font-medium">
              {(input.terms ?? []).join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"contents" in input ? (
          <li>
            <Trans>Content</Trans>:{" "}
            <span className="font-medium">
              {(input.contents ?? []).join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"campaigns" in input ? (
          <li>
            <Trans>Campaigns</Trans>:{" "}
            <span className="font-medium">
              {campaignsSummary.length ? campaignsSummary.join(", ") : "(null)"}
            </span>
          </li>
        ) : null}
        {"defaultChannelGroup" in input ? (
          <li>
            <Trans>Default channel group</Trans>:{" "}
            <span className="font-medium">
              {(input.defaultChannelGroup ?? [])
                .map((channel) => i18n._(DefaultChannelGroupLabels[channel]))
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"events" in input ? (
          <li>
            <Trans>Events (include)</Trans>:{" "}
            <span className="font-medium">
              {(input.events ?? []).join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"eventsNotIn" in input ? (
          <li>
            <Trans>Events (exclude)</Trans>:{" "}
            <span className="font-medium">
              {(input.eventsNotIn ?? []).join(", ") || "(null)"}
            </span>
          </li>
        ) : null}
        {"eventParams" in input ? (
          <li>
            <Trans>Event parameters</Trans>:{" "}
            <span className="font-medium">
              {eventParamsSummary.length
                ? eventParamsSummary.join(", ")
                : "(null)"}
            </span>
          </li>
        ) : null}
        {"expVariantString" in input ? (
          <li>
            <Trans>A/B testing variants</Trans>:{" "}
            <span className="font-medium">
              {(input.expVariantString ?? []).join(", ") || "(null)"}
            </span>
          </li>
        ) : null}

        {"isSessionWithNavigation" in input ? (
          <li>
            <Trans>Sessions with surf activity</Trans>:{" "}
            <span className="font-medium">
              {input.isSessionWithNavigation === null
                ? "(null)"
                : input.isSessionWithNavigation
                  ? i18n._("Yes")
                  : i18n._("No")}
            </span>
          </li>
        ) : null}

        {"pageLocationCount" in input ? (
          <li>
            <Trans>Page location count</Trans>:{" "}
            <span className="font-medium">
              {input.pageLocationCount?.min != null
                ? input.pageLocationCount.min
                : "—"}{" "}
              -{" "}
              {input.pageLocationCount?.max != null
                ? input.pageLocationCount.max
                : "—"}
            </span>
          </li>
        ) : null}

        {"gaSessionNumber" in input ? (
          <li>
            <Trans>Session Number</Trans>:{" "}
            <span className="font-medium">
              {input.gaSessionNumber?.min != null
                ? input.gaSessionNumber.min
                : "—"}{" "}
              -{" "}
              {input.gaSessionNumber?.max != null
                ? input.gaSessionNumber.max
                : "—"}
            </span>
          </li>
        ) : null}

        {input.detailGroupBy ? (
          <li>
            <Trans>Group by</Trans>:{" "}
            <span className="font-medium">
              {detailGroupLabels.length
                ? detailGroupLabels.join(", ")
                : "(null)"}
            </span>
          </li>
        ) : null}
        {input.detailOrderBy ? (
          <li>
            <Trans>Order by</Trans>:{" "}
            <span className="font-medium">
              {detailOrderByLabel ?? "(null)"}
              {detailOrderDirectionLabel ? (
                <span className="ml-2 text-muted-foreground">
                  {detailOrderDirectionLabel}
                </span>
              ) : null}
            </span>
          </li>
        ) : null}
      </ul>
      <div className="mb-3 text-xs text-muted-foreground">
        <Trans>
          Only provided fields will be updated. Omitted fields remain unchanged.
        </Trans>
      </div>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
